import {Link, Route, Routes, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import {Box, Card, CardContent, FormControlLabel, FormGroup, Grid, Switch, Tab, Tabs, Typography} from "@mui/material";
import {useRouteMatch} from "../../../utils/RouteMatch";
import Stores from "./Stores";
import Voices from "./Voices";
import {useClustersRepository} from "../../../api/repositories/ClustersRepository";
import {formatDate} from "../../../utils/FormatUtils";
import {WarningRounded} from "@mui/icons-material";
import FinanceDialog from "../../../component/FinanceDialog";
import FixedVoice from "./FixedVoice";
import SubscriptionConfiguration from "./SubscriptionConfiguration";
import {ID_STANDARD} from "../constants/ClusterStatuses";

const ClusterPreview = (props: any) => {

    const {t} = useTranslation('finance', {keyPrefix: 'clusters'});

    const {id} = useParams();
    const {updateCluster, findAllClusters} = useClustersRepository();

    const [cluster, setCluster] = useState<any>(undefined);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [message, setMessage] = useState('');

    const ROUTING = [
        {id: 0, name: t('preview.tabs.stores'), pathname: `/finance/clusters/${id}`, visible: true},
        {id: 1, name: t('preview.tabs.voices'), pathname: `/finance/clusters/${id}/voices`, visible: true},
        {
            id: 2,
            name: t('preview.tabs.others'),
            pathname: `/finance/clusters/${id}/others`,
            visible: cluster?.type == ID_STANDARD
        },
        {
            id: 3,
            name: t('preview.tabs.configuration'),
            pathname: `/finance/clusters/${id}/configuration`,
            visible: cluster?.type == ID_STANDARD
        },
    ];

    const routeMatch = useRouteMatch(ROUTING.map(it => it.pathname));
    const currentTab = routeMatch?.pattern?.path;


    useEffect(() => {
        if (id !== undefined) {
            findAllClusters().then(data => {
                setCluster(data.result.content.filter((cluster: any) => cluster.id === parseInt(id))[0]);
            })
        }
    }, []);

    return (
        <Card variant="outlined" sx={{margin: '15px'}}>
            <CardContent>
                <Grid container direction={'column'} rowGap={2}>
                    <Grid item>
                        <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                            <Typography variant={'body1'} fontWeight={'bold'}>
                                {t('preview.buttons.back', {id: cluster?.id, name: cluster?.name})}
                            </Typography>
                            <Typography variant="subtitle2" color="textSecondary">
                                ({cluster?.legalEntity || '---'} / {cluster?.dealer || '---'})
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid container>
                        <Grid item xs>
                            <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                                <Typography variant={'body1'}>
                                    {t('preview.top-label.cluster-key')}
                                </Typography>
                                <Typography variant={'body1'} fontWeight={'bold'}>
                                    {t('preview.top-label.cluster-value', {type: t('cluster-types.' + cluster?.type)})}
                                </Typography>
                            </Box>
                            <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                                <Typography variant={'body1'}>
                                    {t('preview.top-label.key')}
                                </Typography>
                                <Typography variant={'body1'} fontWeight={'bold'}>
                                    {t('preview.top-label.value', {
                                        from: formatDate(cluster?.validFrom),
                                        to: formatDate(cluster?.validTo)
                                    })}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs>
                            <FormGroup sx={{alignItems: 'flex-end'}}>
                                <FormControlLabel
                                    control={<Switch checked={cluster?.validStatus === "ACTIVE"} onChange={event => {
                                        let checked = event.target.checked;
                                        if (cluster.id !== null && cluster.id !== undefined) {
                                            updateCluster(parseInt(cluster.id), checked ? "ACTIVE" : "INACTIVE", cluster.msTypology,
                                                cluster.type, cluster.name, cluster.validFrom, cluster.validTo)
                                                .then((res) => {
                                                    if (res.status === 'OK' && res.result.length === 0) {
                                                        setCluster({
                                                            ...cluster,
                                                            validStatus: checked ? "ACTIVE" : "INACTIVE"
                                                        });
                                                    } else {
                                                        let message = t('error-dialog.errors.cluster-activation.header', {count: res.result.length});
                                                        res.result.slice(0, 5).forEach((ms: any) => {
                                                            message += t('error-dialog.errors.cluster-activation.content', {
                                                                msList: ms.value,
                                                                key: ms.key
                                                            })
                                                        })
                                                        message += t('error-dialog.errors.cluster-activation.footer', {count: res.result.length});

                                                        setMessage(message);
                                                        setOpenErrorDialog(true);
                                                    }
                                                });
                                        }
                                    }}/>} label={t('preview.buttons.active')}/>
                            </FormGroup>
                        </Grid>
                    </Grid>
                    <Grid item sx={{borderBottom: 1, borderColor: 'divider'}}>
                        <Tabs value={currentTab ? currentTab : false}>
                            {ROUTING.filter(it => it.visible).map(it => <Tab key={it.id} label={it.name}
                                                                             value={it.pathname} to={it.pathname}
                                                                             component={Link}/>)}
                        </Tabs>
                    </Grid>
                    <Grid item>
                        <Routes>
                            <Route path="/"
                                   element={<Stores legalEntity={cluster?.legalEntity} dealer={cluster?.dealer}/>}/>
                            <Route path="/voices"
                                   element={<Voices legalEntity={cluster?.legalEntity} dealer={cluster?.dealer}
                                                    type={cluster?.type}/>}/>
                            {cluster?.type == ID_STANDARD &&
                                <>
                                    <Route path="/others" element={<FixedVoice legalEntity={cluster?.legalEntity}
                                                                               dealer={cluster?.dealer}/>}/>
                                    <Route path="/configuration"
                                           element={<SubscriptionConfiguration legalEntity={cluster?.legalEntity}
                                                                               dealer={cluster?.dealer}/>}/>
                                </>
                            }
                        </Routes>
                    </Grid>
                </Grid>
            </CardContent>
            <FinanceDialog
                title={t(`error-dialog.errors.cluster-activation.title`)}
                content={message}
                cancelButton={t('error-dialog.close')}
                open={openErrorDialog}
                onClose={() => {
                    setOpenErrorDialog(false)
                }}
                titleIcon={<WarningRounded sx={{color: 'error.main'}}/>}
            />
        </Card>
    )
        ;
}

export default ClusterPreview;
